import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTutorial } from '../../contexts/TutorialContext';
import TutorialCelebration from './TutorialCelebration';
import SimulatedBingoGame from './SimulatedBingoGame';
import SimulatedInbox from './SimulatedInbox';
import SimulatedMarketPurchaseFlow from './SimulatedMarketPurchaseFlow';
import SimulatedWalletFlow from './SimulatedWalletFlow';
import SimulatedBsCalculator from './SimulatedBsCalculator';
import './TutorialOverlay.css';

const TutorialOverlay = () => {
  const {
    isActive,
    currentStep,
    totalSteps,
    tutorialConfig,
    targetRect,
    showCelebration,
    showSimulatedGame,
    showSimulatedModal,
    showSimulatedInbox,
    showSimulatedWallet,
    walletPhase,
    showSimulatedMarketPurchase,
    setMarketPurchasePhase,
    showSimulatedBsCalculator,
    showPrompt,
    promptData,
    hideTutorialPrompt,
    nextStep,
    prevStep,
    skipTutorial,
    advanceAfterAction,
    updateTargetRect,
  } = useTutorial();

  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, width: 280 });
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const step = useMemo(() => {
    if (!tutorialConfig || !tutorialConfig.steps) return null;
    return tutorialConfig.steps[currentStep];
  }, [tutorialConfig, currentStep]);

  // Compute tooltip position based on target rect and desired position
  useEffect(() => {
    if (!isActive || !step) return;

    const vw = windowSize.width || window.innerWidth;
    const vh = windowSize.height || window.innerHeight;
    // Responsive tooltip width: smaller padding on very small screens
    const tooltipWidth = Math.min(vw <= 360 ? vw - 16 : 320, vw - 32);
    // Dynamic height estimate based on text length for better positioning
    const textLen = (step.title?.length || 0) + (step.text?.length || 0);
    const tooltipHeight = Math.min(240, Math.max(140, textLen * 0.6));

    // If a simulated overlay is open, anchor tooltip to top to avoid overlap on mobile
    const hasSimulatedOverlayOpen = showSimulatedInbox || showSimulatedModal || showSimulatedGame || showSimulatedWallet || showSimulatedMarketPurchase || showSimulatedBsCalculator;

    if (!targetRect) {
      // When simulated overlay is open, place tooltip at top with safe margin
      if (hasSimulatedOverlayOpen && vw <= 480) {
        setTooltipPos({
          top: 12,
          left: Math.max(8, vw / 2 - tooltipWidth / 2),
          width: tooltipWidth,
        });
        return;
      }
      // Center of screen
      setTooltipPos({
        top: Math.max(16, vh / 2 - tooltipHeight / 2),
        left: Math.max(8, vw / 2 - tooltipWidth / 2),
        width: tooltipWidth,
      });
      return;
    }

    let top = targetRect.bottom + 12;
    let left = targetRect.centerX - tooltipWidth / 2;

    // Clamp left
    left = Math.max(8, Math.min(left, vw - tooltipWidth - 8));

    // If bottom overflow, place above
    if (top + tooltipHeight > vh - 8) {
      top = targetRect.top - tooltipHeight - 12;
    }
    // If still overflow (target fills screen), center vertically with safe margin
    if (top < 8) {
      top = Math.max(8, vh / 2 - tooltipHeight / 2);
    }

    // Handle explicit position preference
    const pos = step.position || 'bottom';
    if (pos === 'top') {
      top = targetRect.top - tooltipHeight - 12;
      if (top < 8) top = targetRect.bottom + 12;
    } else if (pos === 'left') {
      left = targetRect.left - tooltipWidth - 12;
      if (left < 8) left = targetRect.right + 12;
    } else if (pos === 'right') {
      left = targetRect.right + 12;
      if (left + tooltipWidth > vw - 8) left = targetRect.left - tooltipWidth - 12;
    } else if (pos === 'center') {
      top = Math.max(8, vh / 2 - tooltipHeight / 2);
      left = Math.max(8, vw / 2 - tooltipWidth / 2);
    }

    setTooltipPos({ top: Math.max(8, top), left: Math.max(8, left), width: tooltipWidth });
  }, [isActive, step, targetRect, windowSize, showSimulatedInbox, showSimulatedModal, showSimulatedGame, showSimulatedWallet, showSimulatedMarketPurchase, showSimulatedBsCalculator]);

  // Scroll target into view
  useEffect(() => {
    if (!isActive || !step || !step.target) return;
    const el = document.querySelector(`[data-tutorial-target="${step.target}"]`);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        // Recompute rect after scroll
        setTimeout(() => updateTargetRect(), 400);
      }, 100);
    }
  }, [isActive, currentStep, step, updateTargetRect]);

  const handleNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  // Auto-advance when user clicks the highlighted target element
  useEffect(() => {
    if (!isActive || !step || !step.target) return;
    const el = document.querySelector(`[data-tutorial-target="${step.target}"]`);
    if (!el) return;

    const onClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Let original action (e.g. open inbox) execute first, then advance
      setTimeout(() => advanceAfterAction(), 50);
    };

    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [isActive, step, advanceAfterAction]);

  // Prompt modal (from TutorialAutoTrigger)
  if (showPrompt && promptData) {
    return (
      <div className="tutorial-root">
        <div className="tutorial-backdrop" style={{ zIndex: 9999 }} />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="tutorial-tooltip"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: Math.max(240, Math.min(320, (windowSize.width || window.innerWidth) - 32)),
            zIndex: 10000,
            position: 'fixed',
          }}
        >
          <h3 className="tutorial-title">{promptData.title}</h3>
          <p className="tutorial-text">{promptData.message}</p>
          <div className="tutorial-actions" style={{ justifyContent: 'center', gap: 12 }}>
            <button
              className="tutorial-btn-skip"
              onClick={() => {
                if (typeof promptData.onDecline === 'function') promptData.onDecline();
                hideTutorialPrompt();
              }}
              type="button"
            >
              Ahora no
            </button>
            <button
              className="tutorial-btn-nav primary"
              onClick={() => {
                if (typeof promptData.onAccept === 'function') promptData.onAccept();
                hideTutorialPrompt();
              }}
              type="button"
            >
              Si, mostrar tutorial
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!isActive || !step) return null;

  const isIntroOrCenter = step.action === 'intro' || step.action === 'celebration';
  const hasSimulatedOverlay = showSimulatedInbox || showSimulatedModal || showSimulatedGame || showSimulatedWallet || showSimulatedMarketPurchase || showSimulatedBsCalculator;

  return (
    <div className="tutorial-root">
      {/* Dark overlay with spotlight */}
      <div
        className="tutorial-backdrop"
        style={
          !isIntroOrCenter
            ? { background: 'transparent', pointerEvents: 'none' }
            : undefined
        }
      >
        {targetRect && !isIntroOrCenter && (
          <div
            className="tutorial-spotlight"
            style={{
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
            }}
          />
        )}
      </div>

      {/* Pulsing highlight ring on target */}
      {targetRect && !isIntroOrCenter && (
        <div
          className="tutorial-pulse-ring"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
          }}
        />
      )}

      {/* Animated arrow */}
      {targetRect && !isIntroOrCenter && (
        <TutorialArrow
          targetRect={targetRect}
          tooltipPos={tooltipPos}
          position={step.position || 'bottom'}
        />
      )}

      {/* Tooltip / Card — hidden during celebration overlay */}
      {!showCelebration && (
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 12, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.96 }}
          transition={{ duration: 0.25 }}
          className="tutorial-tooltip"
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            width: tooltipPos.width,
          }}
        >
          {/* Progress dots */}
          <div className="tutorial-progress">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`tutorial-dot ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'done' : ''}`}
              />
            ))}
          </div>

          <h3 className="tutorial-title">{step.title}</h3>
          <p className="tutorial-text">{step.text}</p>

          <div className="tutorial-actions">
            <button
              className="tutorial-btn-skip"
              onClick={skipTutorial}
              type="button"
            >
              Saltar
            </button>

            <div className="tutorial-nav">
              {currentStep > 0 && (
                <button
                  className="tutorial-btn-nav"
                  onClick={prevStep}
                  type="button"
                >
                  Anterior
                </button>
              )}
              {step?.action && ['simulate-game', 'simulate-inbox', 'simulate-modal', 'simulate-wallet', 'simulate-market', 'simulate-bcv-calculator'].includes(step.action) ? (
                <span
                  className="tutorial-btn-nav primary"
                  style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}
                >
                  Juega para continuar
                </span>
              ) : (
                <button
                  className="tutorial-btn-nav primary"
                  onClick={handleNext}
                  type="button"
                >
                  {currentStep === totalSteps - 1 ? 'Finalizar' : 'Siguiente'}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      )}

      {/* Simulated Inbox */}
      <AnimatePresence>
        {showSimulatedInbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tutorial-simulated-inbox"
          >
            <SimulatedInbox
              onAccept={() => {
                advanceAfterAction();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Simulated Bingo Room Modal */}
      <AnimatePresence>
        {showSimulatedModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tutorial-simulated-modal"
          >
            <SimulatedBingoGame
              phase="create-room"
              onRoomCreated={() => {
                advanceAfterAction();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Simulated Bingo Game */}
      <AnimatePresence>
        {showSimulatedGame && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tutorial-simulated-game"
          >
            <SimulatedBingoGame
              phase="play"
              onBingoWon={() => {
                // Auto advance to celebration after delay
                setTimeout(() => advanceAfterAction(), 3000);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Simulated Wallet Flow */}
      <AnimatePresence>
        {showSimulatedWallet && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tutorial-simulated-wallet"
          >
            <SimulatedWalletFlow
              phase={walletPhase}
              onComplete={() => {
                advanceAfterAction();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Simulated Market Purchase Flow */}
      <AnimatePresence>
        {showSimulatedMarketPurchase && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tutorial-simulated-market"
          >
            <SimulatedMarketPurchaseFlow
              onPhaseChange={(phase) => {
                setMarketPurchasePhase(phase);
              }}
              onComplete={() => {
                advanceAfterAction();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Simulated Bs Calculator */}
      <AnimatePresence>
        {showSimulatedBsCalculator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tutorial-simulated-wallet"
          >
            <SimulatedBsCalculator
              onComplete={() => {
                advanceAfterAction();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Celebration */}
      <AnimatePresence>
        {showCelebration && (
          <TutorialCelebration
            title={step?.title}
            subtitle={step?.text}
            durationMs={5000}
            showContinueButton={true}
            onDone={() => {
              advanceAfterAction();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const TutorialArrow = ({ targetRect, tooltipPos, position }) => {
  const arrowLen = 40;
  const [sx, sy, ex, ey] = useMemo(() => {
    let startX = tooltipPos.left + tooltipPos.width / 2;
    let startY = tooltipPos.top;
    let endX = targetRect.centerX;
    let endY = targetRect.top;

    if (position === 'top') {
      startX = tooltipPos.left + tooltipPos.width / 2;
      startY = tooltipPos.top + 180; // approx tooltip height
      endX = targetRect.centerX;
      endY = targetRect.bottom;
    } else if (position === 'left') {
      startX = tooltipPos.left + tooltipPos.width;
      startY = tooltipPos.top + 90;
      endX = targetRect.left;
      endY = targetRect.centerY;
    } else if (position === 'right') {
      startX = tooltipPos.left;
      startY = tooltipPos.top + 90;
      endX = targetRect.right;
      endY = targetRect.centerY;
    }

    // Shorten arrow to not overlap tooltip or target
    const dx = endX - startX;
    const dy = endY - startY;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const padStart = 12;
    const padEnd = 12;
    const ratioStart = padStart / dist;
    const ratioEnd = padEnd / dist;

    return [
      startX + dx * ratioStart,
      startY + dy * ratioStart,
      endX - dx * ratioEnd,
      endY - dy * ratioEnd,
    ];
  }, [targetRect, tooltipPos, position]);

  const midX = (sx + ex) / 2;
  const midY = (sy + ey) / 2;

  return (
    <svg
      className="tutorial-arrow-svg"
      style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9998 }}
    >
      <defs>
        <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L0,6 L9,3 z" fill="#22d3ee" />
        </marker>
      </defs>
      <motion.path
        d={`M ${sx} ${sy} Q ${midX} ${midY} ${ex} ${ey}`}
        stroke="#22d3ee"
        strokeWidth="3"
        fill="none"
        markerEnd="url(#arrowhead)"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      />
    </svg>
  );
};

export default TutorialOverlay;
